import axios from 'axios';

// In-memory fallback for environments where localStorage.setItem throws (e.g. Safari private mode pre-iOS 11).
// Keeps the token alive for the current session even when persistence fails.
let _memoryToken = null;

export function setAuthToken(token) {
  _memoryToken = token || null;
  try {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  } catch (e) {
    // localStorage unavailable — try sessionStorage so token survives page navigation within the tab
    try {
      if (token) {
        sessionStorage.setItem('token', token);
      } else {
        sessionStorage.removeItem('token');
      }
    } catch (e2) { /* both storages unavailable — memory-only */ }
  }
}

export function getAuthToken() {
  if (_memoryToken) return _memoryToken;
  try {
    const t = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (t) { _memoryToken = t; return t; }
  } catch (e) { /* localStorage unavailable */ }
  try {
    const t = sessionStorage.getItem('token') || sessionStorage.getItem('access_token');
    if (t) { _memoryToken = t; return t; }
  } catch (e) { /* sessionStorage unavailable */ }
  return null;
}

export function clearAuthToken() {
  _memoryToken = null;
  try { localStorage.removeItem('token'); } catch (e) { /* ignore */ }
  try { sessionStorage.removeItem('token'); } catch (e) { /* ignore */ }
}

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 12000, // 12s — évite l'écran de chargement infini sur cold start Railway
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Deduplication : une seule tentative de refresh à la fois
let _refreshPromise = null;

async function _doRefresh() {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = axios
    .post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true })
    .then(({ data }) => { if (data?.access_token) setAuthToken(data.access_token); })
    .finally(() => { _refreshPromise = null; });
  return _refreshPromise;
}

api.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry && !original.url?.includes('/auth/')) {
      original._retry = true;
      try {
        await _doRefresh();
        return api(original);
      } catch {
        clearAuthToken();
        return Promise.reject(error);
      }
    }
    if (error.response?.status === 403 && error.response?.data?.error === 'plan_limit_reached') {
      window.dispatchEvent(new CustomEvent('show-upgrade-modal', { detail: error.response.data }));
      error._planLimit = true;
    }
    return Promise.reject(error);
  }
);

export function isPlanLimit(err) {
  return err?._planLimit === true;
}

export function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default api;
